import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowsClockwise, BellRinging, MapPinLine, Camera, GearSix, DeviceMobile } from "@phosphor-icons/react";
import { fmt12 } from "@/components/hr/attendanceStatus";

const POLL_MS = 30000;
const STATE = {
  late: { label: "Late", row: "bg-orange-50/80 border-l-[3px] border-l-[#EA580C]", dot: "bg-[#EA580C]", chip: "bg-[#EA580C] text-white" },
  late_forgiven: { label: "Late · forgiven", row: "bg-amber-50/50 border-l-[3px] border-l-amber-300", dot: "bg-amber-400", chip: "bg-amber-100 text-amber-800" },
  not_in: { label: "Not in yet", row: "border-l-[3px] border-l-slate-200", dot: "bg-slate-300", chip: "bg-slate-100 text-slate-600" },
  in: { label: "In", row: "border-l-[3px] border-l-emerald-400", dot: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-800" },
  out: { label: "Day done", row: "border-l-[3px] border-l-slate-400", dot: "bg-slate-500", chip: "bg-slate-200 text-slate-700" },
  half_day: { label: "Half day", row: "border-l-[3px] border-l-amber-200", dot: "bg-amber-300", chip: "bg-amber-50 text-amber-800" },
  leave: { label: "On leave", row: "border-l-[3px] border-l-rose-300", dot: "bg-rose-400", chip: "bg-rose-100 text-rose-800" },
  off: { label: "Off", row: "border-l-[3px] border-l-slate-100 opacity-70", dot: "bg-slate-200", chip: "bg-slate-50 text-slate-500" },
};
const CHIPS = [["late", "Late"], ["in", "In"], ["not_in", "Not in yet"], ["leave", "On leave"], ["out", "Done"]];

export default function LivePunchBoard({ onOpenSettings }) {
  const [board, setBoard] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async (silent = false) => {
    try {
      const { data } = await api.get("/payroll/punch/today");
      setBoard(data); setUpdatedAt(new Date());
    } catch (e) { if (!silent) toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") load(true); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [load]);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/payroll/late-alerts/send-now");
      const ch = data.alert?.channels || {};
      toast.success(`Alert sent · ${data.alert?.late_count ?? 0} late · in-app ✓${chanTag("email", ch.email)}${chanTag("WhatsApp", ch.whatsapp)}`, { duration: 7000 });
      load(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSending(false); }
  };

  const c = board?.counts || {};
  const rows = (board?.rows || []).filter((r) => filter === "all" || r.state === filter || (filter === "late" && r.state === "late_forgiven"));
  const lateCount = (c.late || 0) + (c.late_forgiven || 0);

  return (
    <div className="border border-slate-200 bg-white rounded-sm flex flex-col h-full" data-testid="live-punch-board">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" /></span>
          <div>
            <div className="text-[12.5px] font-bold text-slate-900 leading-tight">Live · who's in today</div>
            <div className="text-[10px] text-slate-500" data-testid="live-board-date">
              {board ? `${board.weekday}, ${board.date} · ${fmt12(board.time)} IST · shift ${fmt12(board.shift_in)} · grace ${board.late_grace_min} min` : "Loading…"}
              {board?.is_off_day && <span className="ml-1 text-sky-700 font-semibold">· {board.holiday_name || "weekly off"}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {updatedAt && <span className="text-[9.5px] text-slate-400 hidden md:inline">updated {updatedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>}
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => load()} data-testid="live-board-refresh"><ArrowsClockwise size={12} /></Button>
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap px-3 py-2 border-b border-slate-100" data-testid="live-board-chips">
        <Chip active={filter === "all"} onClick={() => setFilter("all")} label="All" value={c.total ?? "—"} cls="bg-slate-100 text-slate-700" testid="all" />
        {CHIPS.map(([k, label]) => {
          const v = k === "late" ? lateCount : c[k] || 0;
          return <Chip key={k} active={filter === k} onClick={() => setFilter(k)} label={label} value={v} cls={v ? STATE[k].chip : "bg-slate-50 text-slate-400"} pulse={k === "late" && v > 0} testid={k} />;
        })}
        {c.outside > 0 && <span className="ml-auto text-[10px] font-semibold text-rose-700 flex items-center gap-1" data-testid="live-board-outside"><MapPinLine size={11} /> {c.outside} outside office</span>}
      </div>

      <div className="flex-1 overflow-y-auto max-h-[380px] divide-y divide-slate-100" data-testid="live-board-rows">
        {!board && <div className="text-center text-slate-400 text-[12px] py-8">Loading…</div>}
        {board && rows.length === 0 && <div className="text-center text-slate-400 text-[12px] py-8">{c.total ? "Nobody in this group" : "No active employees"}</div>}
        {rows.map((r) => <Row key={r.employee.id} r={r} />)}
      </div>

      <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-slate-200 bg-slate-50/70 flex-wrap" data-testid="live-board-alert-footer">
        <div className="text-[10.5px] text-slate-600 leading-tight">
          <AlertStatus alert={board?.alert} channels={board?.channels} />
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={onOpenSettings} data-testid="live-board-alert-settings"><GearSix size={12} className="mr-1" /> Alert settings</Button>
          <Button size="sm" className="h-7 text-[11px] bg-[#0F172A] hover:bg-slate-800 text-white" disabled={sending} onClick={sendNow} data-testid="live-board-send-alert">
            <BellRinging size={12} className="mr-1" /> {sending ? "Sending…" : "Send late alert now"}
          </Button>
        </div>
      </div>
    </div>
  );
}

const chanTag = (name, res) => (!res ? "" : res.status === "sent" ? ` · ${name} ✓` : res.status === "not_configured" ? ` · ${name} not configured` : res.status === "no_recipients" ? "" : ` · ${name} ${res.status}`);

function AlertStatus({ alert, channels }) {
  if (!alert) return <span>Morning digest goes to admins at <b>10:30 AM IST</b> · in-app ✓{channels && !channels.email ? " · e-mail: add SendGrid key" : ""}{channels && !channels.whatsapp ? " · WhatsApp: add Twilio keys" : ""}</span>;
  if (alert.status !== "sent") return <span>Today's alert skipped — {alert.status === "skipped_off_day" ? `${alert.reason || "off day"}` : alert.status === "skipped_all_on_time" ? "everyone on time" : alert.status}</span>;
  const ch = alert.channels || {};
  return (
    <span data-testid="live-board-alert-sent">
      <b className={alert.late_count ? "text-[#EA580C]" : "text-emerald-700"}>Alert sent {fmt12(alert.time)}</b> · {alert.late_count} late · in-app ✓
      <Chan name="e-mail" res={ch.email} /><Chan name="WhatsApp" res={ch.whatsapp} />
      {alert.trigger?.startsWith("manual") && <span className="text-slate-400"> · manual</span>}
    </span>
  );
}

function Chan({ name, res }) {
  if (!res || res.status === "no_recipients") return null;
  const ok = res.status === "sent";
  return <span className={ok ? "" : "text-slate-400"}> · {name} {ok ? "✓" : res.status === "not_configured" ? "not configured" : res.status}</span>;
}

function Chip({ active, onClick, label, value, cls, pulse, testid }) {
  return (
    <button onClick={onClick} className={`h-6 px-2 rounded-sm text-[10.5px] font-semibold flex items-center gap-1.5 border transition-colors ${active ? "border-[#0F172A]" : "border-transparent"} ${cls} ${pulse ? "animate-pulse" : ""}`} data-testid={`live-board-chip-${testid}`}>
      {label} <span className="font-mono-num font-bold">{value}</span>
    </button>
  );
}

function Row({ r }) {
  const m = STATE[r.state];
  const e = r.employee;
  return (
    <div className={`flex items-center gap-2.5 px-3 py-1.5 ${m.row}`} data-testid={`live-row-${e.emp_code}`}>
      <span className={`w-2 h-2 rounded-full shrink-0 ${m.dot}`} />
      <div className="min-w-0 flex-1">
        <div className="text-[12px] font-semibold text-slate-900 truncate leading-tight">{e.name}</div>
        <div className="text-[9.5px] text-slate-500 font-mono-num truncate">{e.emp_code}{e.designation ? ` · ${e.designation}` : ""}{!r.pin_set ? " · no PIN" : ""}</div>
      </div>
      <div className="text-right w-[132px] shrink-0">
        <div className="text-[12px] font-mono-num text-slate-800">{r.in_time ? fmt12(r.in_time) : <span className="text-slate-300">—</span>}{r.out_time ? <span className="text-slate-400"> → {fmt12(r.out_time)}</span> : ""}</div>
        {r.is_late
          ? <div className={`text-[9.5px] font-bold ${r.forgiven ? "text-amber-700" : "text-[#EA580C]"}`} data-testid={`live-late-${e.emp_code}`}>+{r.late_min} min late · {r.forgiven ? "forgiven" : "half-day"}</div>
          : <div className="text-[9.5px] text-slate-400">{m.label}{r.state === "in" && r.late_min > 0 ? ` · ${r.late_min} min in grace` : ""}</div>}
      </div>
      <div className="flex items-center gap-1 w-[52px] justify-end shrink-0 text-slate-400">
        {r.source === "self" && <DeviceMobile size={13} className="text-sky-600" title="self-punched from mobile" />}
        {(r.punch_in?.photo_path || r.punch_out?.photo_path) && <Camera size={13} title="selfie captured" />}
        {r.outside && <MapPinLine size={13} className="text-rose-600" title={`outside office · ${r.punch_in?.distance_m ?? r.punch_out?.distance_m ?? "?"} m from ${r.punch_in?.site_name || r.punch_out?.site_name || "site"}`} />}
      </div>
    </div>
  );
}
